/* ============================================================================
   featureFlags — controle d'activation des fonctionnalites experimentales.

   Resolution :
     1. URL search params (`?ff=quickWinsAlpha,quickWinsZ` / `?nogl2=1`)
     2. localStorage (`marthe.ff.<name>` = '1' | '0')
     3. defaults
     4. respect `prefers-reduced-motion: reduce` (force OFF certains flags)

   Tous les flags sont evalues une fois au mount + reactifs sur changement
   du media query. L'objet retourne par `getFeatureFlags()` est immutable.

   Phase A (Quick Wins canvas 2D) :
     - quickWinsAlpha : alpha modulé par vélocité + decay variation par pinch
     - quickWinsZ     : Z-modulation parallax 3D (plus risque, default OFF
                        avant QA terrain)

   Phase B (Encre Navier-Stokes) :
     - fluidSim       : monte FluidLayer + couplage ASCII↔dye (default OFF)
   ============================================================================ */

export interface FeatureFlags {
  quickWinsAlpha: boolean;
  quickWinsZ: boolean;
  fluidSim: boolean;
  /** Si true, force le fallback canvas 2D meme si WebGL2 est dispo (debug). */
  noGl2: boolean;
  /** Si true, l'utilisateur prefere mouvement reduit (a11y). */
  reducedMotion: boolean;
}

const DEFAULTS: Omit<FeatureFlags, 'reducedMotion'> = {
  quickWinsAlpha: true, // Default ON — fix immediat du multiply fixe
  quickWinsZ: false, // Default OFF — Z bruite, calibrer en QA d'abord
  fluidSim: false, // Default OFF — Phase B mergee mais opt-in
  noGl2: false,
};

function readLocalStorage(name: keyof typeof DEFAULTS): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(`marthe.ff.${name}`);
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function readUrlParams(): Partial<Record<keyof typeof DEFAULTS, boolean>> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Partial<Record<keyof typeof DEFAULTS, boolean>> = {};

  // Format `?ff=name1,name2` active explicitement les flags listes.
  // Format `?ff-name1=1` / `?ff-name1=0` (par flag) supporte aussi.
  const ffList = params.get('ff');
  if (ffList) {
    for (const name of ffList.split(',').map((s) => s.trim())) {
      if (name in DEFAULTS) {
        out[name as keyof typeof DEFAULTS] = true;
      }
    }
  }
  for (const key of Object.keys(DEFAULTS) as (keyof typeof DEFAULTS)[]) {
    const v = params.get(`ff-${key}`);
    if (v === '1' || v === 'true') out[key] = true;
    else if (v === '0' || v === 'false') out[key] = false;
  }

  // Aliases historiques
  if (params.get('nogl2') === '1') out.noGl2 = true;

  return out;
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Retourne un snapshot immutable des flags resolus (priorite URL > LS > default). */
export function getFeatureFlags(): FeatureFlags {
  const url = readUrlParams();
  const reducedMotion = readReducedMotion();

  const resolve = (name: keyof typeof DEFAULTS): boolean => {
    if (url[name] !== undefined) return url[name]!;
    const ls = readLocalStorage(name);
    if (ls !== null) return ls;
    return DEFAULTS[name];
  };

  let quickWinsAlpha = resolve('quickWinsAlpha');
  let fluidSim = resolve('fluidSim');

  // a11y : reduced motion désactive les modulations dynamiques expressives
  // et le fluid sim. Quick wins Z reste OFF par defaut donc pas concerne.
  if (reducedMotion) {
    quickWinsAlpha = false;
    fluidSim = false;
  }

  return Object.freeze({
    quickWinsAlpha,
    quickWinsZ: resolve('quickWinsZ'),
    fluidSim,
    noGl2: resolve('noGl2'),
    reducedMotion,
  });
}

/** Cache memoize au mount (les flags ne changent pas mid-session). */
let cached: FeatureFlags | null = null;
export function getFeatureFlagsCached(): FeatureFlags {
  if (!cached) cached = getFeatureFlags();
  return cached;
}

/** Force un refresh (utile pour tests ou panel admin live). */
export function resetFeatureFlagsCache(): void {
  cached = null;
}
